'use client'

import { useEffect, useRef, useState, CSSProperties } from "react";
import RadixTooltip from "react-radix-tooltip";
import SvgIcons from "./SvgIcons";
import Link from "next/link";
import './ShareContent.css';

const { ShareIcon, ContentCopyIcon, LinkedInIcon, WhatsAppIcon, RedditIcon, PinterestIcon, XIcon, FacebookIcon, MessengerIcon }  = SvgIcons();

type ShareContentProps = {
    url?: string;
    title?: string;
    tooltip?: string;
    style?: CSSProperties;
    fontSize?: string;
    fontWeight?: string;
    fontColor?: string;
    tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
    shareIcon?: React.ReactNode;
    className?: string;
}

const ShareContent = (
    {
        url = "",
        title = "",
        tooltip = "",
        style = {},
        fontSize = "18px",
        fontWeight = "normal",
        fontColor = "#FFF",
        tooltipPlacement = "top",
        shareIcon = <ShareIcon width="24px" height="24px" />,
        className = ""
    }: ShareContentProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (!ref.current?.contains(event.target as Node)) {
                const el = ref?.current;

                if (!el || el.contains(event.target as Node)) {
                    return;
                }

                setIsOpen(false);
            }
        };

        window?.addEventListener("click", handleClick, { capture: true });

        return () => {
            window?.removeEventListener("click", handleClick, { capture: true });
        };
    }, [ref]);

    const handleShareClick = async (type: string) => {
        if (type === 'copy') {
            await navigator.clipboard.writeText(url);
        }

        setIsOpen(false);
        return true;
    }

    const toggle = () => setIsOpen(old => !old);
    const transitionClass = isOpen ? "flex" : "hidden";


    return (
        <div className={`relative ${className}`} ref={ref} style={style}>
            <RadixTooltip title={tooltip.length > 0 ? tooltip : title} placement={tooltipPlacement} >
                <button
                    onClick={toggle}
                    className="cursor-pointer"
                    style={{ padding: 0, margin: 0, minWidth: 0, color: '#FFF' }}
                >
                    {shareIcon}
                </button>
            </RadixTooltip>
            <div className={`shareContent absolute top-8 overflow-hidden right-2 z-30 flex flex-col py-0 bg-gray-900 rounded-lg shadow-xl ${transitionClass}`}>
                    <div style={{ padding: '6px 15px', margin: 0, fontSize: fontSize, fontWeight: fontWeight, color: fontColor, textAlign: 'left' }}>
                        {title}
                        <span className='float-right cursor-pointer hover:text-red-500 transition duration-300' onClick={() => setIsOpen(false)}>X</span>
                    </div>
                    <hr className="mb-2 h-0.5 border-t-0 bg-white/10" />
                    <div className="flex flex-row mb-3 gap-1 justify-center items-center pr-2">
                        <RadixTooltip title="Copy Link" placement="top" >
                            <a
                                onClick={(e) => { handleShareClick('copy'); e.preventDefault(); }}
                                rel="noreferrer" className="copy"
                            >
                                <ContentCopyIcon width='24px' height='24px' />
                            </a>
                        </RadixTooltip>
                        <div className="inline-block w-0.5 self-stretch bg-white/10"></div>
                        <RadixTooltip title="Facebook" placement="top" >
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank"
                                onClick={() => { handleShareClick('fb'); }}
                                rel="noreferrer" className="facebook"
                            >
                                <FacebookIcon width='24px' height='24px' />
                            </a>
                        </RadixTooltip>
                        <div className="inline-block w-0.5 self-stretch bg-white/10"></div>
                        <RadixTooltip title="Twitter" placement="top" >
                            <Link
                                href={`https://x.com/intent/tweet?url=${encodeURIComponent(url)}`} target="_blank"
                                onClick={() => { handleShareClick('x'); }}
                                rel="noreferrer" className="x"
                            >
                                <XIcon width='24px' height='24px' />
                            </Link>
                        </RadixTooltip>
                        <div className="inline-block w-0.5 self-stretch bg-white/10"></div>
                        <RadixTooltip title="Whatsapp" placement="top" >
                            <Link
                                href={`https://web.whatsapp.com://send?text=${encodeURIComponent(url)}`} target="_blank"
                                onClick={() => { handleShareClick('whatsapp'); }}
                                rel="noreferrer" className="whatsapp"
                            >
                                <WhatsAppIcon width='24px' height='24px' />
                            </Link>
                        </RadixTooltip>
                        <div className="inline-block w-0.5 self-stretch bg-white/10"></div>
                        <RadixTooltip title="Pinterest" placement="top" >
                            <Link
                                href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}`} target="_blank"
                                onClick={() => { handleShareClick('pinterest'); }}
                                rel="noreferrer" className="pinterest"
                            >
                                <PinterestIcon width='24px' height='24px' />
                            </Link>
                        </RadixTooltip>
                        <div className="inline-block w-0.5 self-stretch bg-white/10"></div>
                        <RadixTooltip title="Messenger" placement="top" >
                            <Link
                                href={`fb-messenger://share/?link=${encodeURIComponent(url)}`} target="_blank"
                                onClick={() => { handleShareClick('fb-messenger'); }}
                                rel="noreferrer" className="fb-messenger"
                            >
                                <MessengerIcon width='24px' height='24px' />
                            </Link>
                        </RadixTooltip>
                        <div className="inline-block w-0.5 self-stretch bg-white/10"></div>
                        <RadixTooltip title="LinkedIn" placement="top" >
                            <Link
                                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}`} target="_blank"
                                onClick={() => { handleShareClick('linkedin'); }}
                                rel="noreferrer" className="linkedin"
                            >
                                <LinkedInIcon width='24px' height='24px' />
                            </Link>
                        </RadixTooltip>
                        <div className="inline-block w-0.5 self-stretch bg-white/10"></div>
                        <RadixTooltip title="Reddit" placement="top" >
                            <Link
                                href={`https://www.reddit.com/submit?url=${encodeURIComponent(url)}`} target="_blank"
                                onClick={() => { handleShareClick('reddit'); }}
                                rel="noreferrer" className="reddit"
                            >
                                <RedditIcon width='24px' height='24px' />
                            </Link>
                        </RadixTooltip>
                    </div>
            </div>
        </div>
    );
}


export default ShareContent;